import { closeBooks, reopenBooks, useGetClosing } from '@myrentalmate/api-client';
import { Button, Chip, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { useState } from 'react';

import { FormDialog } from '../../components/FormDialog';
import { required, type Field } from '../../components/FormFields';
import { okData } from '../../lib/apiData';
import { formatDate } from '../../lib/format';
import { useCan } from '../../lib/permissions';

const REOPEN_FIELDS: Field[] = [
  { name: 'reason', label: 'Why It Needs Reopening', required: true },
];

/** The month-end after this one, when the books are closed through this one. */
function nextMonthEnd(closedThrough: string): string {
  const [year, month] = closedThrough.split('-').map(Number);
  // Day 0 of a month is the last day of the one before, and months here count from 0.
  const last = new Date(year, month + 1, 0);
  const paddedMonth = String(last.getMonth() + 1).padStart(2, '0');
  return `${last.getFullYear()}-${paddedMonth}-${String(last.getDate()).padStart(2, '0')}`;
}

export function ClosingPage() {
  const status = okData(useGetClosing().data);
  const canClose = useCan('books.close');
  const canReopen = useCan('org.manage');
  const [dialog, setDialog] = useState<'close' | 'reopen' | null>(null);
  if (!status) return <CircularProgress aria-label="Loading" />;

  const closed = status.closed_through;
  // One month at a time from the last close. With no close yet, the most recent finished month.
  const next = closed ? nextMonthEnd(closed) : status.last_closable;
  const canCloseNext = next <= status.last_closable;

  return (
    <Stack spacing={2} sx={{ maxWidth: 640 }}>
      <div>
        <Typography variant="h5">Closing the Books</Typography>
        <Typography color="text.secondary">
          Once a month is closed, nothing can be posted into it, so your reports for it can't
          change.
        </Typography>
      </div>
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack spacing={2} sx={{ alignItems: 'flex-start' }}>
          <Chip
            label={closed ? `Closed through ${formatDate(closed)}` : 'No months closed yet'}
            color={closed ? 'success' : 'default'}
          />
          <Stack direction="row" spacing={1}>
            {canClose && canCloseNext && (
              <Button variant="contained" onClick={() => setDialog('close')}>
                Close through {formatDate(next)}
              </Button>
            )}
            {canReopen && closed && (
              <Button color="error" onClick={() => setDialog('reopen')}>
                Reopen last month
              </Button>
            )}
          </Stack>
          {!canCloseNext && (
            <Typography color="text.secondary">
              The next month can be closed once it's over.
            </Typography>
          )}
        </Stack>
      </Paper>
      {dialog === 'close' && (
        <FormDialog
          title={`Close Through ${formatDate(next)}?`}
          intro="This locks that month and every month before it. Check the trial balance first. Corrections after this are dated in an open month."
          fields={[]}
          initial={{}}
          submitLabel="Close the books"
          onSubmit={() => closeBooks({ through: next })}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === 'reopen' && (
        <FormDialog
          title="Reopen the Last Closed Month?"
          intro="This reopens one month, and earlier months stay closed. Reports you've already given anyone for that month may change. The reason is kept in the audit log."
          fields={REOPEN_FIELDS}
          initial={{ reason: '' }}
          submitLabel="Reopen"
          onSubmit={(values) => reopenBooks({ reason: required(values, 'reason') })}
          onClose={() => setDialog(null)}
        />
      )}
    </Stack>
  );
}
